import classNames from "classnames";
import type { Toast, ToastOptions } from "react-hot-toast";
import toast from "react-hot-toast";
import { MdCancel } from "react-icons/md";

type IToast = {
  message: string;
  toastVisible: boolean;
  toastId: string;
  onClose: (toastId: string) => void;
};

export const SuccessToast = ({
  message,
  toastVisible,
  onClose,
  toastId,
}: IToast) => (
  <button
    className={classNames(
      "!z-[9999999999] !fixed bg-[#F7FDF3] text-[18px] text-black leading-[127%] mb-2 flex h-auto w-[calc(100vw-32px)] sm:w-[664px] font-outfit items-center space-x-2 rounded-[12px] p-[12px] font-normal border border-[#48AD00]",
      toastVisible && "cursor-pointer",
    )}
    onClick={() => onClose(toastId)}
  >
    <span>
      <svg
        width="26"
        height="24"
        viewBox="0 0 26 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13 24C8.6875 24 4.75 21.75 2.59375 18C0.4375 14.2969 0.4375 9.75 2.59375 6C4.75 2.29688 8.6875 0 13 0C17.2656 0 21.2031 2.29688 23.3594 6C25.5156 9.75 25.5156 14.2969 23.3594 18C21.2031 21.75 17.2656 24 13 24ZM18.2969 9.79688H18.25C18.7188 9.375 18.7188 8.67188 18.25 8.20312C17.8281 7.78125 17.125 7.78125 16.7031 8.20312L11.5 13.4531L9.29688 11.25C8.82812 10.7812 8.125 10.7812 7.70312 11.25C7.23438 11.6719 7.23438 12.375 7.70312 12.7969L10.7031 15.7969C11.125 16.2656 11.8281 16.2656 12.2969 15.7969L18.2969 9.79688Z"
          fill="#48AD00"
        />
      </svg>
    </span>
    <p className="text-left flex-1 mr-auto">{message}</p>

    <span className="mt-0.5 text-[16px] cursor-pointer leading-[127%] font-normal px-[16px] py-[6px] border border-[#E2E2E2] inline-block rounded-[8px] bg-white shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.02),_0px_-2px_1px_0px_rgba(0,_0,_0,_0.07)_inset]">
      Close
    </span>
  </button>
);

export const ErrorToast = ({
  message,
  toastVisible,
  onClose,
  toastId,
}: IToast) => (
  <button
    className={classNames(
      "!z-[9999999999] !fixed bg-red-50 text-[18px] text-black leading-[127%] mb-2 flex h-auto w-[calc(100vw-32px)] sm:w-[664px] font-outfit items-center space-x-2 rounded-[12px] p-[12px] font-normal border border-[#E53A14]",
      toastVisible && "cursor-pointer",
    )}
    onClick={() => onClose(toastId)}
  >
    <span>
      <MdCancel className="bg-red-50" color="#E53A14" size={24} />
    </span>
    <p className="text-left flex-1 mr-auto">{message}</p>

    <span className="mt-0.5 text-[16px] cursor-pointer leading-[127%] font-normal px-[16px] py-[6px] border border-[#E2E2E2] inline-block rounded-[8px] bg-white shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.02),_0px_-2px_1px_0px_rgba(0,_0,_0,_0.07)_inset]">
      Close
    </span>
  </button>
);

export const WarningToast = ({
  message,
  toastVisible,
  onClose,
  toastId,
}: IToast) => (
  <button
    className={classNames(
      "bg-error text-white mb-2 flex items-center h-auto w-[calc(100vw-32px)] sm:w-auto space-x-2 rounded-md p-4 text-[12px] font-medium",
      toastVisible && "cursor-pointer",
    )}
    onClick={() => onClose(toastId)}
  >
    <span className="mt-0.5">
      <MdCancel name="info" className="h-4 w-4" />
    </span>
    <p data-testid="toast-warning" className="text-left">
      {message}
    </p>
  </button>
);

export const DefaultToast = ({
  message,
  toastVisible,
  onClose,
  toastId,
}: IToast) => (
  <button
    className={classNames(
      "animate-fade-in-up bg-brand-default text-brand mb-2 flex h-auto space-x-2 rounded-md p-3 text-sm font-semibold shadow-md rtl:space-x-reverse md:max-w-sm",
      toastVisible && "animate-fade-in-up cursor-pointer",
    )}
    onClick={() => onClose(toastId)}
  >
    <span className="mt-0.5">
      <MdCancel name="check" className="h-4 w-4" />
    </span>
    <p data-testid="toast-default" className="text-left">
      {message}
    </p>
  </button>
);

const TOAST_VISIBLE_DURATION = 3000;

type ToastVariants = "success" | "warning" | "error";

// eslint-disable-next-line react-refresh/only-export-components
export const customToast = (
  message: string,
  variant: ToastVariants,

  options: number | ToastOptions = TOAST_VISIBLE_DURATION,
) => {
  const _options: ToastOptions =
    typeof options === "number" ? { duration: options } : options;
  if (!_options.duration) _options.duration = TOAST_VISIBLE_DURATION;
  if (!_options.position) _options.position = "top-center";
  if (!_options.style) _options.style = { zIndex: 99999 };

  const onClose = (toastId: string) => {
    toast.remove(toastId);
  };

  const toastElements: { [x in ToastVariants]: (t: Toast) => JSX.Element } = {
    success: (t) => (
      <SuccessToast
        message={message}
        toastVisible={t.visible}
        onClose={onClose}
        toastId={t.id}
      />
    ),
    error: (t) => (
      <ErrorToast
        message={message}
        toastVisible={t.visible}
        onClose={onClose}
        toastId={t.id}
      />
    ),
    warning: (t) => (
      <WarningToast
        message={message}
        toastVisible={t.visible}
        onClose={onClose}
        toastId={t.id}
      />
    ),
  };

  const toastId = toast.custom(
    toastElements[variant] ||
      ((t) => (
        <DefaultToast
          message={message}
          toastVisible={t.visible}
          onClose={onClose}
          toastId={t.id}
        />
      )),
    _options,
  );

  setTimeout(() => {
    toast.remove(toastId);
  }, _options.duration);

  return toastId;
};
